import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { CarService } from 'src/app/services/car/car.service';
import { UpdateCarModalService } from 'src/app/services/update-car-modal/update-car-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-update-car',
  templateUrl: './update-car.component.html',
  styleUrls: ['./update-car.component.scss']
})
export class UpdateCarComponent extends DestructorComponent implements OnInit {

  @Output() carUpdated = new EventEmitter<void>();

  carForm: FormGroup;
  isLoading: boolean = false;
  selectedCar: any = null;

  constructor(
    private fb: FormBuilder,
    private _carService: CarService,
    private _updateCarModalService: UpdateCarModalService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.carForm = this.fb.group({
      car_length: ['', [Validators.required]],
      car_width: ['', [Validators.required]],
      car_height: ['', [Validators.required]],
      inventory_no: [{ value: '', disabled: true }],
      name: [{ value: '', disabled: true }],
      company_name: [{ value: '', disabled: true }],
      year: [{ value: '', disabled: true }]
    });
  }

  // Getter methods for form controls
  get car_length() { return this.carForm.get('car_length'); }
  get car_width() { return this.carForm.get('car_width'); }
  get car_height() { return this.carForm.get('car_height'); }
  get inventory_no() { return this.carForm.get('inventory_no'); }
  get name() { return this.carForm.get('name'); }
  get company_name() { return this.carForm.get('company_name'); }
  get year() { return this.carForm.get('year'); }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_update_car');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceCar = this._updateCarModalService.getSelectedCar();
        if (serviceCar) {
          this.selectedCar = serviceCar;
          this.populateForm(this.selectedCar);
        } else {
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._updateCarModalService.clearSelectedCar();
        this.resetForm();
      });
    }
  }

  private populateForm(car: any): void {
    if (car) {
      this.carForm.patchValue({
        car_length: car.car_length || '',
        car_width: car.car_width || '',
        car_height: car.car_height || '',
        inventory_no: car.car_no || car.inventory_no || '',
        name: car.name || '',
        company_name: car.company_name || '',
        year: car.year || ''
      });
    }
  }

  private resetForm(): void {
    this.carForm.reset();
    this.isLoading = false;
    this.selectedCar = null;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_update_car');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  onSubmit(): void {
    if (this.carForm.invalid) {
      this.markFormGroupTouched(this.carForm);
      return;
    }

    this.isLoading = true;

    const formData = {
      car_length: this.carForm.get('car_length')?.value,
      car_width: this.carForm.get('car_width')?.value,
      car_height: this.carForm.get('car_height')?.value
    };

    this.updateCar(formData);
  }

  private updateCar(formData: any): void {
    const carId = this.selectedCar._id || this.selectedCar.id;
    this.subscriptions.push(
      this._carService.UpdateData(carId, formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Car updated successfully.',
              icon: 'success',
              confirmButtonText: 'OK',
              buttonsStyling: false,
              customClass: {
                confirmButton: 'btn btn-primary'
              }
            }).then(() => {
              this.closeModal();
              this.carUpdated.emit();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to update car.',
              icon: 'error',
              confirmButtonText: 'OK',
              buttonsStyling: false,
              customClass: {
                confirmButton: 'btn btn-primary'
              }
            });
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error updating car:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while updating the car.',
            icon: 'error',
            confirmButtonText: 'OK',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-primary'
            }
          });
        }
      })
    );
  }
}
